import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  useMediaQuery,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
} from "@mui/material";
import {
  ArrowDropDown,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import Search from "../../../components/common/search/Search";
import Input from "../../../components/common/input/Input";
import axiosInstance from "../../../utils/axios/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { userApiLoader } from "../../../action/loaderActions";
import { toast } from "react-toastify";
import loaderGif from "../../../assets/images/loading.gif";
import { useNavigate } from "react-router-dom";

const formatTime = (time24) => {
  if (!time24) return "";
  const [h, m] = time24.split(":");
  const d = new Date();
  d.setHours(+h);
  d.setMinutes(+m);
  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const parseTimeTo24 = (time12h) => {
  if (!time12h) return "";
  const [timePart, modifier] = time12h.split(" ");
  if (!modifier) return "";
  let [hours, minutes] = timePart.split(":");
  if (hours === "12") hours = "00";
  if (modifier.toUpperCase() === "PM") hours = String(parseInt(hours, 10) + 12);
  return `${hours.padStart(2, "0")}:${minutes}`;
};

const Volunteeroppertunity = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get user data from Redux state
  const userFromState = useSelector(state => state.user || {});

  // Extract user ID from JWT token if not available in state
  const getUserIdFromToken = () => {
    try {
      const token = userFromState.accessToken;
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub;
      }
    } catch (error) {
      console.error("Error decoding JWT token:", error);
    }
    return null;
  };

  // Get user ID from state or JWT token
  const currentUserId = userFromState?.UserId ||
    userFromState?.userId ||
    userFromState?.id ||
    getUserIdFromToken();

  console.log("👤 Current User ID:", currentUserId);

  // State variables
  const [data, setData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("");
  const [settings, setSettings] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form states
  const [eventCreatedAt, setEventCreatedAt] = useState("");
  const [eventCategory, setEventCategory] = useState("");
  const [eventName, setEventName] = useState("");
  const [eventLocation, setEventLocation] = useState("");
  const [eventDescription, setEventDescription] = useState("");
  const [duration, setDuration] = useState("");
  const [campusRole, setCampusRole] = useState("");
  const [eventImage, setEventImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [upcomingData, setUpcomingData] = useState([]);
  const [errors, setErrors] = useState({
    eventName: "",
    eventCategory: "",
    eventDate: "",
    eventTime: "",
    eventDescription: "",
    eventLocation: "",
    duration: "",
    campusRole: "",
    eventImage: "",
  });

  // Today's date for min attribute
  const todayString = new Date().toISOString().split("T")[0];
  const [eventDate, setEventDate] = useState(todayString);
  const [eventTime, setEventTime] = useState(() => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const isLgScreen = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  useEffect(() => {
    fetchCategories();
    fetchOpportunities();
    fetchUpcomingOpportunities();
  }, []);

  // Function to check if current user is the opportunity creator
  const isOpportunityCreator = (opportunity) => {
    if (opportunity.created_by && currentUserId) {
      return opportunity.created_by?.toString() === currentUserId?.toString();
    }
    return false;
  };

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/categories`,
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
          },
        }
      );
      setCategories(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOpportunities = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities`,
        {},
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
          },
        }
      );
      const apiData = response?.data?.data?.data || [];
      console.log("📊 Opportunities List Response:", apiData);
      
      const formatted = apiData.map((item) => ({
        id: item.id,
        title: item.name,
        name: item.name,
        date: item.date,
        time: item.time,
        location: item.location,
        duration: item.duration,
        campus_role: item.campus_role,
        image: item.image || item.image_url || null,
        category: item.category,
        description: item.description,
        created_by: item.created_by,
        is_attending: item.is_attending,
        people_attending: item.people_attending,
        status: item.status
      }));

      setData({ data: { data: formatted } });
      setSortedData(formatted);

      // Check if any opportunity has status 1
      const disabledOpportunity = formatted.find(opp => opp.status === 1);
      if (disabledOpportunity) {
        toast.warning(`Opportunity "${disabledOpportunity.name}" is Disabled`, { autoClose: 6000 });
      }
    } catch (error) {
      console.error("Error fetching opportunities:", error);
      toast.error("Failed to fetch opportunities");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUpcomingOpportunities = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/upcoming`,
        {},
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
          },
        }
      );
      const apiData = response?.data?.data?.data || [];
      
      // Filter out opportunities created by current user
      const filteredOpportunities = apiData.filter(
        (opportunity) => opportunity.created_by?.toString() !== currentUserId?.toString()
      );

      const formatted = filteredOpportunities.map((item) => ({
        id: item.id,
        title: item.name,
        name: item.name,
        date: item.date,
        time: item.time,
        location: item.location,
        duration: item.duration,
        campus_role: item.campus_role,
        image: item.image || null,
        attendees: item.people_attending || 0,
        description: item.description || "",
        responseStatus: item.responseStatus || "",
        is_attending: item.is_attending,
        people_attending: item.people_attending
      }));
      
      setUpcomingData(formatted);
    } catch (error) {
      console.error("Error fetching upcoming opportunities:", error);
      toast.error("Failed to fetch upcoming opportunities");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (filterValue) => {
    setSelectedFilter(filterValue);
    if (!filterValue) {
      setSortedData(data?.data?.data || []);
      return;
    }
    const filtered = (data?.data?.data || []).filter(
      (item) => item.category === filterValue
    );
    setSortedData(filtered);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
    const filteredData = (data?.data?.data || []).filter((row) =>
      Object.values(row).some((val) =>
        String(val).toLowerCase().includes(value.toLowerCase())
      )
    );
    setSortedData(filteredData);
  };

  const fetchSingleOpportunity = async (id) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/view`,
        { id },
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer ${userFromState.accessToken}`,
          },
        }
      );
      if (response?.data?.status === 200) return response.data.data;
      toast.warning("Failed to fetch details");
      return null;
    } catch (error) {
      toast.error("Error fetching opportunity detail");
      console.error(error);
      return null;
    }
  };

 

  // Navigation function for View Participants
const handleViewParticipants = async (opportunity) => {
  console.log("🔥 View Participants clicked for:", opportunity);
  
  try {
    setIsLoading(true);
    
    const response = await axiosInstance.post(
      `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/view`,
      { id: opportunity.id },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
          Accept: "application/json",
          Authorization: `Bearer YOUR_TOKEN_HERE`,
        },
      }
    );

    console.log("✅ API Response:", response.data);

    if (response.data.status === 200) {
      // Navigate to participants page with the actual API data structure
      navigate(`/opportunities/${opportunity.id}/participants`, {
        state: {
          opportunityData: response.data.data, // This is the actual data from your API
          originalOpportunity: opportunity     // Keep the original for reference
        }
      });

      toast.success('Opportunity details loaded successfully');
    } else {
      console.log("❌ API returned non-200 status:", response.data);
      toast.error("Failed to load opportunity details");
    }
    
  } catch (error) {
    console.error('💥 API Error:', error);
    toast.error('Failed to load opportunity details');
    
    // Fallback navigation with original opportunity data
    navigate(`/opportunities/${opportunity.id}/participants`, {
      state: {
        opportunityData: opportunity,
        originalOpportunity: opportunity,
        error: 'Failed to load detailed information from API'
      }
    });
  } finally {
    setIsLoading(false);
  }
};

  const handleEditClick = async (id) => {
    setIsLoading(true);
    const data = await fetchSingleOpportunity(id);
    setIsLoading(false);
    if (!data) return;

    setEventName(data.name);
    setEventDate(data.date);

    let normalizedTime = data.time;
    if (typeof normalizedTime === "string") {
      normalizedTime = normalizedTime.replace(/(AM|PM)$/i, " $1").trim();
    }
    setEventTime(parseTimeTo24(normalizedTime));

    setEventDescription(data.description || "");
    setEventLocation(data.location || "");
    setCampusRole(data.campus_role || "");
    setDuration(data.duration || "");
    setEventCategory(data.category || "");
    setEventImage(null);
    setEventCreatedAt(data.created_at || "");

    setEditingId(data.id);
    setIsEditing(true);
    setSettings(true);
  };

  const validateForm = () => {
    const newErrors = {
      eventName: "",
      eventCategory: "",
      eventDate: "",
      eventTime: "",
      eventDescription: "",
      eventLocation: "",
      duration: "",
      campusRole: "",
      eventImage: "",
    };

    let isValid = true;
    if (!eventName.trim()) {
      newErrors.eventName = "Opportunity name is required.";
      isValid = false;
    }
    if (!eventCategory) {
      newErrors.eventCategory = "Category is required.";
      isValid = false;
    }
    if (!eventDate) {
      newErrors.eventDate = "Date is required.";
      isValid = false;
    }
    if (!eventTime) {
      newErrors.eventTime = "Time is required.";
      isValid = false;
    }
    if (!eventDescription.trim()) {
      newErrors.eventDescription = "Description is required.";
      isValid = false;
    }
    if (!eventLocation.trim()) {
      newErrors.eventLocation = "Location is required.";
      isValid = false;
    }
    if (!duration) {
      newErrors.duration = "Duration is required.";
      isValid = false;
    }
    if (!campusRole) {
      newErrors.campusRole = "Campus role is required.";
      isValid = false;
    }
    if (!eventImage && !isEditing) {
      newErrors.eventImage = "Image is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const clearFormAndErrors = () => {
    setEditingId(null);
    setEventName("");
    setEventCategory("");
    setEventDescription("");
    setEventLocation("");
    setEventImage(null);
    setEventDate(todayString);
    const now = new Date();
    setEventTime(now.toTimeString().slice(0, 5));
    setDuration("");
    setCampusRole("");
    setEventCreatedAt("");
    setErrors({
      eventName: "",
      eventCategory: "",
      eventDate: "",
      eventTime: "",
      eventDescription: "",
      eventLocation: "",
      duration: "",
      campusRole: "",
      eventImage: "",
    });
    setIsEditing(false);
  };

  const handleUpdateOpportunity = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    dispatch(userApiLoader({ isApiLoader: true }));
    const formData = new FormData();
    formData.append("id", editingId);
    formData.append("name", eventName);
    formData.append("date", eventDate);
    formData.append("time", formatTime(eventTime));
    formData.append("description", eventDescription);
    formData.append("location", eventLocation);
    formData.append("campus_role", campusRole);
    formData.append("duration", duration);
    formData.append("category", eventCategory);
    formData.append("created_at", eventCreatedAt);
    formData.append("updated_at", new Date().toISOString());
    if (eventImage) formData.append("image", eventImage);

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/edit`,
        formData,
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      if (response?.data?.status === 200) {
        toast.success("Opportunity updated successfully.");
        clearFormAndErrors();
        setSettings(false);
        fetchOpportunities();
      } else {
        console.warn("EDIT returned status:", response.data);
        toast.warning("Could not update opportunity. Check console for details.");
      }
    } catch (error) {
      console.error("Error in handleUpdateOpportunity():", error);
      toast.error("Failed to update opportunity");
    } finally {
      setSubmitting(false);
      dispatch(userApiLoader({ isApiLoader: false }));
    }
  };

  const handleAddOpportunity = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    dispatch(userApiLoader({ isApiLoader: true }));
    const formData = new FormData();
    formData.append("name", eventName);
    formData.append("date", eventDate);
    formData.append("time", formatTime(eventTime));
    formData.append("description", eventDescription);
    formData.append("location", eventLocation);
    formData.append("campus_role", campusRole);
    formData.append("duration", duration);
    formData.append("category", eventCategory);
    formData.append("created_at", new Date().toISOString());
    formData.append("updated_at", new Date().toISOString());
    if (eventImage) formData.append("image", eventImage);

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/create`,
        formData,
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const createdOpportunity = response?.data?.data?.[0];
      if (createdOpportunity) {
        setSortedData((prev) => [createdOpportunity, ...prev]);
        setData((prev) => ({
          ...prev,
          data: {
            ...prev?.data,
            data: [createdOpportunity, ...(prev?.data?.data || [])],
          },
        }));
      }

      toast.success(response?.data?.message || "Opportunity created successfully");
      clearFormAndErrors();
      setSettings(false);
      setSelectedFilter("");
      setCurrentPage(0);
    } catch (error) {
      console.error("Error in handleAddOpportunity():", error);
      toast.error("Failed to create opportunity");
    } finally {
      setSubmitting(false);
      dispatch(userApiLoader({ isApiLoader: false }));
    }
  };

  const handleDeleteOpportunity = async (id) => {
    if (!window.confirm("Are you sure you want to delete this opportunity?")) return;
    
    dispatch(userApiLoader({ isApiLoader: true }));
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/${id}`,
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
          },
        }
      );
      if (response?.data?.status === 200) {
        toast.success("Opportunity deleted successfully.");
        fetchOpportunities();
      } else {
        console.warn("DELETE returned status:", response.data);
        toast.warning("Could not delete opportunity. Check console for details.");
      }
    } catch (error) {
      console.error("Error in handleDeleteOpportunity():", error);
      toast.error("Failed to delete opportunity");
    } finally {
      dispatch(userApiLoader({ isApiLoader: false }));
    }
  };

  const duplicateOpportunity = async (opportunity) => {
    dispatch(userApiLoader({ isApiLoader: true }));
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/duplicate`,
        { id: opportunity.id },
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
          },
        }
      );

      const duplicatedOpportunity = response?.data?.data?.[0];
      if (duplicatedOpportunity) {
        setSortedData((prevSorted) => [
          ...(prevSorted || []),
          duplicatedOpportunity,
        ]);
        setData((prevData) => {
          if (!prevData || !prevData.data || !prevData.data.data) return prevData;
          return {
            ...prevData,
            data: {
              ...prevData.data,
              data: [...prevData.data.data, duplicatedOpportunity],
            },
          };
        });
        toast.success(response?.data?.message || "Opportunity duplicated successfully");
      } else {
        toast.error("Duplicate response missing opportunity data");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to duplicate opportunity");
    } finally {
      dispatch(userApiLoader({ isApiLoader: false }));
    }
  };

  const handleResponse = async (opportunityId, responseValue) => {
    const responseInt = parseInt(responseValue);
    console.log("🎯 RSVP - Opportunity ID:", opportunityId);
    console.log("🎯 RSVP - Response Value:", responseInt);

    setIsLoading(true);

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/respond`,
        {
          id: opportunityId,
          response: responseInt,
        },
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
          },
        }
      );

      console.log("✅ RSVP API Response:", response.data);
      toast.success(response?.data?.message || "Response recorded successfully!");

      // Remove the opportunity from upcomingData list
      setUpcomingData((prevOpportunities) =>
        prevOpportunities.filter((o) => o.id !== opportunityId)
      );

      // Find the opportunity and add it to "My Opportunities" with correct status
      const opportunity = upcomingData.find(o => o.id === opportunityId);
      if (opportunity) {
        const opportunityForMyList = {
          ...opportunity,
          people_attending: responseInt,
          is_attending: responseInt
        };

        // Add to sortedData
        setSortedData(prev => [opportunityForMyList, ...prev]);

        // Add to main data
        setData(prevData => ({
          ...prevData,
          data: {
            ...prevData?.data,
            data: [opportunityForMyList, ...(prevData?.data?.data || [])]
          }
        }));

        console.log("✅ Opportunity manually added to My Opportunities with status:", responseInt);
      }
    } catch (error) {
      console.error("💥 RSVP API Error:", error?.response?.data || error);
      toast.error(error?.response?.data?.message || "Failed to record response");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (opportunity, responseValue) => {
    try {
      setIsLoading(true);

      console.log("🚀 Sending API request for opportunity:", opportunity.id);
      console.log("🚀 With response value:", responseValue);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_COMMUNITY_API_BASE_URL}v1/opportunities/respond`,
        {
          id: opportunity.id,
          response: responseValue
        },
        {
          headers: {
            "Ocp-Apim-Subscription-Key": "6476f278e86f4fc6a4643077bc489c05",
            Accept: "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
          },
        }
      );

      console.log("✅ API Response:", response.data);

      if (response.data.status === 200 || response.status === 200) {
        if (responseValue === 2) {
          // "No, I'm not attending" - Remove from My Opportunities, add to Upcoming
          console.log("📤 Moving opportunity to upcoming opportunities");

          const updatedSortedOpportunities = sortedData.filter(o => o.id !== opportunity.id);
          setSortedData(updatedSortedOpportunities);

          const updatedMainData = {
            ...data,
            data: {
              ...data?.data,
              data: data?.data?.data?.filter(o => o.id !== opportunity.id) || []
            }
          };
          setData(updatedMainData);

          // Add to upcoming opportunities
          const opportunityForUpcoming = {
            ...opportunity,
            people_attending: 0,
            is_attending: null
          };
          setUpcomingData(prev => [opportunityForUpcoming, ...prev]);

          toast.success("Status updated! Opportunity moved to upcoming opportunities.");
        } else {
          // Update status in current list
          console.log("🔄 Updating opportunity status in current list");

          const updatedSortedOpportunities = sortedData.map(o => {
            if (o.id === opportunity.id) {
              console.log(`Updating opportunity ${o.id} from ${o.is_attending} to ${responseValue}`);
              return { ...o, is_attending: responseValue };
            }
            return o;
          });

          const updatedMainData = {
            ...data,
            data: {
              ...data?.data,
              data: data?.data?.data?.map(o => {
                if (o.id === opportunity.id) {
                  return { ...o, is_attending: responseValue };
                }
                return o;
              }) || []
            }
          };

          console.log("📊 Updated sortedData:", updatedSortedOpportunities.find(o => o.id === opportunity.id));

          setSortedData(updatedSortedOpportunities);
          setData(updatedMainData);

          toast.success("Status updated successfully!");
        }
      } else {
        console.log("❌ API returned non-200 status:", response.data);
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("💥 Status change error:", error);
      toast.error("Failed to update status. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const attendanceStatusMap = {
    1: "Yes, Will be Attending",
    2: "May be Attending",
  };

  // Function to get proper status text for the current user
  const getStatusText = (opportunity) => {
    // If current user is the creator, ALWAYS show "Created by Me"
    if (isOpportunityCreator(opportunity)) {
      return "Created by Me";
    }

    // If current user is not the creator, show their attendance status
    return attendanceStatusMap[opportunity.people_attending] || "No Response";
  };

  const handleCloseForm = () => {
    clearFormAndErrors();
    setSettings(false);
  };

  return (
    <Grid container spacing={2}>
      <div className="container">
        <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div className="card-body">
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                  <div className="d-flex align-items-center">
                    <h3 className="mb-2">My Volunteering Opportunities</h3>
                  </div>
                  <div className="d-flex align-items-center flex-wrap mb-1">
                    <button 
                      onClick={() => {
                        setIsEditing(false);
                        clearFormAndErrors();
                        setSettings(true);
                      }}
                      className="!w-fit !px-3 !h-10"
                      disabled={submitting}
                      style={{
                        backgroundColor: '#ce55b9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '2px 24px',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontFamily: '"Saira", sans-serif',
                        opacity: submitting ? 0.6 : 1
                      }}
                    >
                      {submitting ? 'Processing...' : '+ Add Opportunity'}
                    </button>
                  </div>
                </div>

                {/* Filter Panel */}
                <div className="row filter-panel mt-4">
                  <div className="col-md-4">
                    <div className="serarch-bar">
                      <Search
                        value={searchQuery}
                        handleInputChange={(e) => handleSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-6"></div>
                  <div className="col-md-2">
                    <div className="w-full flex items-center justify-end gap-2">
                      <FormControl fullWidth className="filter">
                        <InputLabel style={{ lineHeight: "14px" }}>Filter</InputLabel>
                        <Select
                          value={selectedFilter}
                          onChange={(e) => handleSort(e.target.value)}
                          label="Filter"
                          IconComponent={(props) => (
                            <ArrowDropDown {...props} style={{ color: "#c942b1" }} />
                          )}
                          style={{ height: 40 }}
                        >
                          <MenuItem value="">None</MenuItem>
                          {categories.length > 0 &&
                            categories.map((cat) => (
                              <MenuItem key={cat.id} value={cat.id}>
                                {cat.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="row">
                  <div className="col-md-12">
                    <div className="table-responsive mb-0">
                      {isLoading ? (
                        <div className="w-full flex justify-center items-center py-10">
                          <img src={loaderGif} alt="Loading..." className="loading-icon-img" />
                        </div>
                      ) : (
                        <TableContainer className="table table-hover table-bordered mb-0 text-md-nowrap text-lg-nowrap text-xl-nowrap table-striped">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>S.No</TableCell>
                                <TableCell>Image</TableCell>
                                <TableCell>Opportunity Name</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Time</TableCell>
                                <TableCell>Location</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>People Attending</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedData.length > 0 ? (
                                sortedData
                                  .slice(
                                    currentPage * rowsPerPage,
                                    currentPage * rowsPerPage + rowsPerPage
                                  )
                                  .map((opportunity, idx) => (
                                    <TableRow
                                      key={opportunity.id || idx}
                                      className={opportunity.status === 1 ? "disabled-row" : ""}
                                    >
                                      <TableCell>{currentPage * rowsPerPage + idx + 1}</TableCell>
                                      <TableCell>
                                        {opportunity.image ? (
                                          <img
                                            src={opportunity.image}
                                            alt={opportunity.title}
                                            className="w-[100px] h-[70px] rounded-md"
                                          />
                                        ) : (
                                          <Typography variant="body2">No Image</Typography>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {opportunity.title || opportunity.name}
                                        {opportunity.status === 1 && (
                                          <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                                            This opportunity is disabled by admin
                                          </div>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        {categories.find((cat) => cat.id === opportunity.category)?.name || "N/A"}
                                      </TableCell>
                                      <TableCell>{opportunity.date}</TableCell>
                                      <TableCell>{opportunity.time}</TableCell>
                                      <TableCell>{opportunity.location || "N/A"}</TableCell>
                                      <TableCell>
                                        {opportunity.duration === 1
                                          ? "Short"
                                          : opportunity.duration === 2
                                          ? "Long"
                                          : "N/A"}
                                      </TableCell>
                                      <TableCell>
                                        {opportunity.campus_role === 1
                                          ? "On Campus"
                                          : opportunity.campus_role === 2
                                          ? "Off Campus"
                                          : "N/A"}
                                      </TableCell>
                                      <TableCell>{opportunity.people_attending || 0}</TableCell>
                                      <TableCell>
                                        {isOpportunityCreator(opportunity) ? (
                                          // Show non-editable status for opportunity creators
                                          <span className="font-medium text-blue-600">
                                            Created by Me
                                          </span>
                                        ) : (
                                          // Show dropdown for opportunity participants
                                          <FormControl size="small" sx={{ minWidth: 160 }}>
                                            <Select
                                              value={(() => {
                                                const attending = opportunity.is_attending;
                                                console.log(`Opportunity ${opportunity.id} is_attending:`, attending, typeof attending);

                                                // Convert to number and handle all cases
                                                const numValue = parseInt(attending);

                                                if (numValue === 1) return 1;  // Yes, Will Attend
                                                if (numValue === 2) return 2;  // No, I'm not attending  
                                                if (numValue === 3) return 3;  // Maybe Attending
                                                return 1; // Default to "Yes, Will Attend"
                                              })()}
                                              onChange={(e) => {
                                                const selectedValue = parseInt(e.target.value);
                                                console.log("🎯 Selected dropdown value:", selectedValue);
                                                console.log("🎯 Opportunity before update:", opportunity);
                                                handleStatusChange(opportunity, selectedValue);
                                              }}
                                              sx={{ fontSize: '0.875rem' }}
                                            >
                                              <MenuItem value={1}>
                                                <span className="flex items-center gap-2">
                                                  ✅ Yes, Will Attend
                                                </span>
                                              </MenuItem>
                                              <MenuItem value={3}>
                                                <span className="flex items-center gap-2">
                                                  🤔 Maybe Attending
                                                </span>
                                              </MenuItem>
                                              <MenuItem value={2}>
                                                <span className="flex items-center gap-2">
                                                  ❌ No, I'm not attending
                                                </span>
                                              </MenuItem>
                                            </Select>
                                          </FormControl>
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        <div className="flex items-center gap-1 active-btn">
                                          {/* Creator-only actions */}
                                          {isOpportunityCreator(opportunity) && (
                                            <>
                                              {/* Edit Button - Only for Opportunity Creators */}
                                              <div
                                                onClick={() => handleEditClick(opportunity.id)}
                                                className="btn btn-indigo btn-icon"
                                                title="Edit Opportunity"
                                              >
                                                <EditIcon color="primary" fontSize="small" />
                                              </div>

                                              {/* Delete Button - Only for Opportunity Creators */}
                                              <div
                                                onClick={() => handleDeleteOpportunity(opportunity.id)}
                                                className="btn btn-denger btn-icon"
                                                title="Delete Opportunity"
                                              >
                                                <DeleteIcon color="primary" fontSize="small" />
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={12} align="center">
                                    <p className="p-4 text-center text-gray-400">
                                      No Opportunities found
                                    </p>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                          <TablePagination
                            component="div"
                            count={sortedData.length}
                            page={currentPage}
                            onPageChange={(e, newPage) => setCurrentPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                              setRowsPerPage(parseInt(e.target.value, 10));
                              setCurrentPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                          />
                        </TableContainer>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Opportunities Section */}
      <div className="container">
        <div className="row">
          <div className="col-md-12">
            <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
              <div className="top-sec">
                <div className="card custom-card">
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                      <div className="d-flex align-items-center">
                        <h3 className="mt-2">Upcoming Volunteering Opportunities</h3>
                      </div>
                    </div>

                    {/* opportunity list */}
                    {upcomingData.length > 0 ? (
                      upcomingData.map((opportunity, idx) => (
                        <div key={idx} className="col-md-12">
                          <div className="upcoming-event-list">
                            <div className="row">
                              <div className="col-md-2">
                                <div className="image-show">
                                  <img
                                    src={opportunity.image}
                                    alt={opportunity.name}
                                  />
                                </div>
                              </div>
                              <div className="col-md-10">
                                <div className="content-area">
                                  <ul>
                                    <li>
                                      {/* Calendar Icon */}
                                      <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" className="h-6 w-6">
                                        <defs>
                                          <style type="text/css">{`
                                            .cls-1 { fill: #fff; }
                                            .cls-2 { fill: #1394f6; }
                                          `}</style>
                                        </defs>
                                        <title />
                                        <g data-name="filled outline" id="filled_outline">
                                          <rect
                                            className="cls-1"
                                            height="50"
                                            rx="5"
                                            transform="translate(66.5 2.5) rotate(90)"
                                            width="41"
                                            x="11.5"
                                            y="9.5"
                                          />
                                          <path
                                            className="cls-2"
                                            d="M7,24V19a5,5,0,0,1,5-5H52a5,5,0,0,1,5,5v5Z"
                                          />
                                          <path
                                            className="cls-1"
                                            d="M20,19a2,2,0,0,1-2-2V11a2,2,0,0,1,4,0v6A2,2,0,0,1,20,19Z"
                                          />
                                          <path
                                            className="cls-1"
                                            d="M44,19a2,2,0,0,1-2-2V11a2,2,0,0,1,4,0v6A2,2,0,0,1,44,19Z"
                                          />
                                          <path
                                            d="M52,13H47V11a3,3,0,0,0-6,0v2H23V11a3,3,0,0,0-6,0v2H12a6,6,0,0,0-6,6V50a6,6,0,0,0,6,6H52a6,6,0,0,0,6-6V19A6,6,0,0,0,52,13Zm-9-2a1,1,0,0,1,2,0v6a1,1,0,0,1-2,0ZM19,11a1,1,0,0,1,2,0v6a1,1,0,0,1-2,0Zm-7,4h5v2a3,3,0,0,0,6,0V15H41v2a3,3,0,0,0,6,0V15h5a4,4,0,0,1,4,4v5H8V19A4,4,0,0,1,12,15ZM52,54H12a4,4,0,0,1-4-4V26H56V50A4,4,0,0,1,52,54ZM20,32v4H16V32h4m2-2H14v8h8V30ZM20,44v4H16V44h4m2-2H14v8h8V42Zm28-7H26V33H50Zm0,12H26V45H50Z"
                                            fill="#000"
                                          />
                                        </g>
                                      </svg>
                                      {opportunity.date}
                                    </li>
                                    <li>
                                      {/* Clock Icon */}
                                      <svg
                                        viewBox="0 0 64 64"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-6 w-6"
                                      >
                                        <g data-name="filled outline" id="filled_outline">
                                          <path
                                            fill="#1394f6"
                                            d="M41.61,13.79a8.19,8.19,0,0,1,11.5,11.5Z"
                                          />
                                          <path
                                            fill="#1394f6"
                                            d="M10.89,25.29a8.19,8.19,0,0,1,11.5-11.5Z"
                                          />
                                          <circle fill="#1394f6" cx="32" cy="34" r="17" />
                                          <circle
                                            fill="#fff"
                                            cx="32"
                                            cy="34"
                                            r="13"
                                            transform="translate(-2.05 65.95) rotate(-89.92)"
                                          />
                                          <polygon
                                            fill="currentColor"
                                            points="39.29 42.71 31 34.41 31 26 33 26 33 33.59 40.71 41.29 39.29 42.71"
                                          />
                                          <path
                                            fill="currentColor"
                                            d="M17.35,11.06a9.19,9.19,0,0,0-6.5,15.69l13-13A9.19,9.19,0,0,0,17.35,11.06Zm-5.09,4.1A7.22,7.22,0,0,1,20.82,14l-9.77,9.77A7.21,7.21,0,0,1,12.26,15.16Z"
                                          />
                                          <path
                                            fill="currentColor"
                                            d="M53.15,13.75a9.2,9.2,0,0,0-13,0l13,13A9.19,9.19,0,0,0,53.15,13.75Zm-.2,10L43.18,14A7.19,7.19,0,0,1,53,23.72Z"
                                          />
                                          <path
                                            fill="currentColor"
                                            d="M50,34A18,18,0,0,0,33,16.05V11h3V9H28v2h3v5.05a18,18,0,0,0-8.42,33.27l-3.41,5.13,1.66,1.1,3.52-5.27a17.87,17.87,0,0,0,15.3,0l3.52,5.27,1.66-1.1-3.41-5.13A18,18,0,0,0,50,34ZM32,50A16,16,0,1,1,48,34,16,16,0,0,1,32,50Z"
                                          />
                                        </g>
                                      </svg>
                                      {opportunity.time}
                                    </li>
                                    <li><strong>Volunteers:</strong> {opportunity.people_attending}</li>
                                  </ul>

                                  <h2> {opportunity.name}</h2>
                                  <p>{opportunity.description}</p>
                                  <select
                                    placeholder="Respond"
                                    value={opportunity.is_attending?.toString() ?? "0"}
                                    onChange={(e) =>
                                      handleResponse(opportunity, e.target.value)
                                    }
                                  >
                                    <option value="0">Respond</option>
                                    <option value="1">Yes, Will be Attending</option>
                                    <option value="3">Maybe Attending</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="p-4 text-center text-gray-400 w-full">
                        No Upcoming Volunteering Opportunities found
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Modal - Updated to match Notes UI Design */}
      {settings && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '450px',
          maxWidth: '90vw',
          backgroundColor: 'white',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Saira", sans-serif'
        }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{
              background: '#ce55b9',
              color: 'white',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h5 style={{ 
                  fontSize: '20px', 
                  fontWeight: 600, 
                  fontFamily: '"Saira", sans-serif',
                  margin: 0
                }}>
                  {isEditing ? 'Edit Opportunity' : 'Add New Opportunity'}
                </h5>
              </div>
              <button
                onClick={handleCloseForm}
                disabled={submitting}
                style={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              padding: '24px',
              overflow: 'auto',
              background: '#ffffff'
            }}>
              {/* Opportunity Name */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Opportunity Name *
                </label>
                <input
                  type="text"
                  value={eventName}
                  onChange={(e) => {
                    setEventName(e.target.value);
                    setErrors((prev) => ({ ...prev, eventName: "" }));
                  }}
                  disabled={submitting}
                  placeholder="Enter opportunity name"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.eventName ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: errors.eventName ? 'rgba(244, 67, 54, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: errors.eventName ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {errors.eventName || 'Enter a descriptive name for your opportunity'}
                </div>
              </div>

              {/* Category */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Category *
                </label>
                <select
                  value={eventCategory}
                  onChange={(e) => {
                    setEventCategory(e.target.value);
                    setErrors((prev) => ({ ...prev, eventCategory: "" }));
                  }}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.eventCategory ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: errors.eventCategory ? 'rgba(244, 67, 54, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select a category</option>
                  {categories.length > 0 &&
                    categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                </select>
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: errors.eventCategory ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {errors.eventCategory || 'Select the category that best describes your opportunity'}
                </div>
              </div>

              {/* Date */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Date *
                </label>
                <input
                  type="date"
                  value={eventDate}
                  min={todayString}
                  onChange={(e) => {
                    setEventDate(e.target.value);
                    setErrors((prev) => ({ ...prev, eventDate: "" }));
                  }}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.eventDate ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: errors.eventDate ? 'rgba(244, 67, 54, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: errors.eventDate ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {errors.eventDate || 'Select the date for your opportunity'}
                </div>
              </div>

              {/* Time */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Time *
                </label>
                <input
                  type="time"
                  value={eventTime}
                  onChange={(e) => {
                    setEventTime(e.target.value);
                    setErrors((prev) => ({ ...prev, eventTime: "" }));
                  }}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.eventTime ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: errors.eventTime ? 'rgba(244, 67, 54, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: errors.eventTime ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {errors.eventTime || 'Select the time for your opportunity'}
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Description *
                </label>
                <textarea
                  value={eventDescription}
                  onChange={(e) => {
                    setEventDescription(e.target.value);
                    setErrors((prev) => ({ ...prev, eventDescription: "" }));
                  }}
                  disabled={submitting}
                  placeholder="Enter opportunity description"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.eventDescription ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: errors.eventDescription ? 'rgba(244, 67, 54, 0.05)' : 'white',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: errors.eventDescription ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {errors.eventDescription || 'Provide details about your opportunity'}
                </div>
              </div>

              {/* Location */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Location *
                </label>
                <input
                  type="text"
                  value={eventLocation}
                  onChange={(e) => {
                    setEventLocation(e.target.value);
                    setErrors((prev) => ({ ...prev, eventLocation: "" }));
                  }}
                  disabled={submitting}
                  placeholder="Enter location"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.eventLocation ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: errors.eventLocation ? 'rgba(244, 67, 54, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: errors.eventLocation ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {errors.eventLocation || 'Where will this opportunity take place?'}
                </div>
              </div>

              {/* Duration */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Duration *
                </label>
                <select
                  value={duration}
                  onChange={(e) => {
                    setDuration(e.target.value);
                    setErrors((prev) => ({ ...prev, duration: "" }));
                  }}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.duration ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: errors.duration ? 'rgba(244, 67, 54, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select duration</option>
                  <option value={1}>Short</option>
                  <option value={2}>Long</option>
                </select>
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: errors.duration ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {errors.duration || 'How long will this opportunity last?'}
                </div>
              </div>

              {/* Campus Role */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Campus Role *
                </label>
                <select
                  value={campusRole}
                  onChange={(e) => {
                    setCampusRole(e.target.value);
                    setErrors((prev) => ({ ...prev, campusRole: "" }));
                  }}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.campusRole ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: errors.campusRole ? 'rgba(244, 67, 54, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select campus role</option>
                  <option value={1}>On Campus</option>
                  <option value={2}>Off Campus</option>
                </select>
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: errors.campusRole ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {errors.campusRole || 'Is this opportunity on or off campus?'}
                </div>
              </div>

              {/* Image Upload */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Upload Image {!isEditing && '*'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    setEventImage(e.target.files[0]);
                    setErrors((prev) => ({ ...prev, eventImage: "" }));
                  }}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.eventImage ? '2px solid #f44336' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: errors.eventImage ? 'rgba(244, 67, 54, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: errors.eventImage ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {errors.eventImage || 'Upload an image for your opportunity'}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div style={{
              padding: '24px',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              background: 'white',
              display: 'flex',
              gap: '16px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCloseForm}
                disabled={submitting}
                style={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: '"Saira", sans-serif',
                  padding: '12px 24px',
                  minWidth: '100px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={isEditing ? handleUpdateOpportunity : handleAddOpportunity}
                disabled={submitting}
                style={{
                  background: '#ce55b9',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: '"Saira", sans-serif',
                  padding: '12px 24px',
                  minWidth: '140px',
                  color: 'white',
                  border: 'none',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ fontFamily: '"Saira", sans-serif' }}>
                      {isEditing ? 'Updating...' : 'Creating...'}
                    </span>
                  </>
                ) : (
                  <span style={{ fontFamily: '"Saira", sans-serif' }}>
                    {isEditing ? 'Update Opportunity' : 'Add Opportunity'}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay */}
      {settings && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1200
          }}
          onClick={handleCloseForm}
        />
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .disabled-row {
          background-color: #ffebee !important;
          opacity: 0.7;
        }
        
        .active-btn {
          display: flex;
          gap: 8px;
        }
        
        .btn {
          padding: 8px;
          border-radius: 4px;
          border: 1px solid #ddd;
          background-color: #f5f5f5;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .btn:hover {
          background-color: #e0e0e0;
        }
        
        .btn-info {
          background-color: #2196f3;
          border-color: #2196f3;
        }
        
        .btn-success {
          background-color: #4caf50;
          border-color: #4caf50;
        }
        
        .btn-indigo {
          background-color: #3f51b5;
          border-color: #3f51b5;
        }
        
        .btn-denger {
          background-color: #f44336;
          border-color: #f44336;
        }
        
        .upcoming-event-list {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          margin-bottom: 16px;
          padding: 16px;
          background: white;
        }
        
        .image-show img {
          width: 100%;
          height: 120px;
          object-fit: cover;
          border-radius: 8px;
        }
        
        .content-area ul {
          list-style: none;
          padding: 0;
          margin: 0 0 16px 0;
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }
        
        .content-area li {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #666;
        }
        
        .content-area h2 {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
        }
        
        .content-area p {
          margin: 0 0 16px 0;
          color: #666;
          line-height: 1.5;
        }
        
        .content-area select {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
          font-size: 14px;
        }
      `}</style>
    </Grid>
  );
};

export default Volunteeroppertunity;